import { FormField, DateInput, SkeletonList, isolate } from '@batiflow/ui';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  MessageBar,
  MessageBarBody,
  Radio,
  RadioGroup,
  Select,
  __css,
  tokens,
} from '@fluentui/react-components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotify } from '@/shared/hooks/use-notify';
import { useOrganisationMembers } from '../api';
import type { DocumentDetail } from '../document-model';
import { isStepUpRequired } from '../model';
import { useApprovalTemplates, useRefreshApprovals, useStartApproval } from './approvals-api';
import {
  approvalErrorMessage,
  draftProblem,
  newStep,
  officeNamesOf,
  startBody,
  todayInTunis,
  type StepDraft,
} from './approvals-model';
import { StepsEditor } from './steps-editor';
import { StepsSummary } from './steps-summary';

const useStyles = __css({"body":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1noh358"},"hint":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","sj55zd":"fkfq4zb"},"summary":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f778t4r","Dbcxam":0,"rjzwhg":0,"Bblux5w":"f9b8bpc","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"f12n3xlw","Beyfa6y":0,"Bbmb7ep":0,"Btl43ni":0,"B7oj6ja":0,"Dimara":"ft85np5","De3pzq":"f1dmdbja"},"summaryTitle":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","Be2twd7":"fkhj508","Bhrd7zp":"fl43uef"},"due":{"D305fb":"f4tgp3f"}});

type Source = 'template' | 'custom';
const FROM_TEMPLATE: Source = 'template';
const FROM_SCRATCH: Source = 'custom';

export interface RequestApprovalDialogProps {
  orgId: string;
  document: DocumentDetail;
  onClose: () => void;
}

/**
 * "Request approval": the current version of the document goes to reviewers through the steps of
 * a template of the office, or through steps built here (sequential or parallel, reviewers from
 * the office's people), with an optional due date. A summary says who decides when.
 */
export function RequestApprovalDialog({ orgId, document, onClose }: RequestApprovalDialogProps) {
  const { t } = useTranslation('documents');
  const styles = useStyles();
  const notify = useNotify();
  const members = useOrganisationMembers(orgId);
  const templates = useApprovalTemplates(orgId);
  const start = useStartApproval();
  const refresh = useRefreshApprovals(document.spaceId);

  const [chosenSource, setSource] = useState<Source | null>(null);
  const [templateId, setTemplateId] = useState('');
  const [steps, setSteps] = useState<StepDraft[]>(() => [newStep()]);
  const [dueOn, setDueOn] = useState('');
  const [touched, setTouched] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const people = members.data?.items ?? [];
  const nameOf = (userId: string) =>
    people.find((member) => member.userId === userId)?.displayName ??
    t('approvals.stepper.unknownReviewer');
  const templateList = templates.data?.items ?? [];
  const source: Source = chosenSource ?? (templateList.length > 0 ? 'template' : 'custom');
  const template = templateList.find((item) => item.id === templateId);

  const problem =
    source === 'template' ? (template ? null : 'templateRequired') : draftProblem(steps);
  const label = isolate(document.title);

  const submit = async () => {
    setTouched(true);
    if (problem || start.isPending) return;
    setFailure(null);
    try {
      await start.mutateAsync({
        spaceId: document.spaceId,
        documentId: document.id,
        data: startBody(
          source === 'template' && template ? { templateId: template.id } : { steps },
          dueOn,
        ),
      });
      await refresh();
      notify({ intent: 'success', message: t('approvals.request.done', { title: label }) });
      onClose();
    } catch (error) {
      if (isStepUpRequired(error)) return;
      setFailure(approvalErrorMessage(t, error));
    }
  };

  const summarySteps = source === 'template' ? (template?.steps ?? []) : steps;
  const officeNames = officeNamesOf(templateList);

  return (
    <Dialog
      open
      modalType="modal"
      onOpenChange={(_event, data) => {
        if (!data.open && !start.isPending) onClose();
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t('approvals.request.title', { title: label })}</DialogTitle>
          <DialogContent>
            <div className={styles.body}>
              <p className={styles.hint}>{t('approvals.request.intro')}</p>

              {templates.isPending || members.isPending ? (
                <SkeletonList count={3} />
              ) : (
                <>
                  {templateList.length > 0 ? (
                    <RadioGroup
                      aria-label={t('approvals.request.sourceLabel')}
                      value={source}
                      onChange={(_event, data) => {
                        setSource(data.value === 'template' ? 'template' : 'custom');
                        setFailure(null);
                      }}
                    >
                      <Radio value={FROM_TEMPLATE} label={t('approvals.request.fromTemplate')} />
                      <Radio value={FROM_SCRATCH} label={t('approvals.request.fromScratch')} />
                    </RadioGroup>
                  ) : templates.isError ? (
                    <MessageBar intent="warning" layout="multiline">
                      <MessageBarBody>{t('approvals.request.templatesFailed')}</MessageBarBody>
                    </MessageBar>
                  ) : null}

                  {source === 'template' ? (
                    <FormField
                      label={t('approvals.request.templateLabel')}
                      required
                      error={
                        touched && problem === 'templateRequired'
                          ? t('approvals.request.templateRequired')
                          : null
                      }
                    >
                      <Select
                        value={templateId}
                        onChange={(_event, data) => {
                          setTemplateId(data.value);
                        }}
                      >
                        <option value="">{t('approvals.request.templatePlaceholder')}</option>
                        {templateList.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                      </Select>
                    </FormField>
                  ) : (
                    <>
                      <StepsEditor
                        steps={steps}
                        onChange={(next) => {
                          setSteps(next);
                          setFailure(null);
                        }}
                        members={people}
                        membersFailed={members.isError}
                        disabled={start.isPending}
                      />
                      {touched && problem === 'noReviewer' ? (
                        <MessageBar intent="error" layout="multiline">
                          <MessageBarBody>{t('approvals.steps.noReviewerProblem')}</MessageBarBody>
                        </MessageBar>
                      ) : null}
                    </>
                  )}

                  <DateInput
                    label={t('approvals.request.dueLabel')}
                    hint={t('approvals.request.dueHint')}
                    value={dueOn}
                    min={todayInTunis()}
                    onChange={setDueOn}
                  />

                  {summarySteps.some(
                    (step) => step.reviewerIds.length + step.reviewerOrgIds.length > 0,
                  ) ? (
                    <section
                      className={styles.summary}
                      aria-label={t('approvals.request.summaryTitle')}
                    >
                      <h3 className={styles.summaryTitle}>{t('approvals.request.summaryTitle')}</h3>
                      <StepsSummary
                        steps={summarySteps}
                        nameOf={nameOf}
                        officeNameOf={(orgId) => officeNames.get(orgId)}
                      />
                      <p className={styles.hint}>{t('approvals.request.summaryRules')}</p>
                    </section>
                  ) : null}
                </>
              )}

              {failure ? (
                <MessageBar intent="error" layout="multiline">
                  <MessageBarBody>{failure}</MessageBarBody>
                </MessageBar>
              ) : null}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={start.isPending}>
              {t('common:actions.cancel')}
            </Button>
            <Button
              appearance="primary"
              disabledFocusable={start.isPending}
              onClick={() => {
                void submit();
              }}
            >
              {start.isPending ? t('approvals.request.sending') : t('approvals.request.send')}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}


import "/opt/batiflow/app/apps/web/src/features/documents/approvals/request-approval-dialog.griffel.css";